import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  Box,
  Typography
} from '@mui/material';
import Cropper from 'react-easy-crop';
import { useAuth } from '../contexts/AuthContext';

interface LogoCropDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (upload: LogoUploadResult) => void;
  imageFile: File;
}

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface LogoUploadResult {
  key: string;
  logoId: string;
  width: number;
  height: number;
  format: 'png' | 'jpeg';
  size: number;
}

type PresetId = 'original' | 'wide' | 'landscape' | 'square' | 'tall';

const CROP_PRESETS: Array<{ id: PresetId; label: string; aspect?: number }> = [
  { id: 'original', label: 'Original' },
  { id: 'wide', label: 'Wide 5:1', aspect: 5 / 1 },
  { id: 'landscape', label: 'Landscape 3:1', aspect: 3 / 1 },
  { id: 'square', label: 'Square 1:1', aspect: 1 / 1 },
  { id: 'tall', label: 'Tall 1:2', aspect: 1 / 2 }
];

const btnSx = { borderRadius: 2, textTransform: 'none', fontWeight: 600 } as const;

function clampCropArea(area: CropArea, image: HTMLImageElement) {
  const x = Math.max(0, Math.round(area.x));
  const y = Math.max(0, Math.round(area.y));
  const width = Math.max(1, Math.min(Math.round(area.width), image.naturalWidth - x));
  const height = Math.max(1, Math.min(Math.round(area.height), image.naturalHeight - y));
  return { x, y, width, height };
}

export function LogoCropDialog({ open, onClose, onSave, imageFile }: LogoCropDialogProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CropArea | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [originalAspect, setOriginalAspect] = useState(1);
  const [presetId, setPresetId] = useState<PresetId>('original');
  const { currentBusiness, API_BASE_URL, token, csrfToken, isCookieMode, fetchCsrfToken } = useAuth();

  useEffect(() => {
    if (!open || !imageFile) return undefined;

    const url = URL.createObjectURL(imageFile);
    const img = new Image();

    setImageUrl(url);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setPresetId('original');
    setCroppedAreaPixels(null);
    setError(null);

    img.onload = () => {
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        setOriginalAspect(img.naturalWidth / img.naturalHeight);
      }
    };
    img.src = url;

    return () => {
      URL.revokeObjectURL(url);
      setImageUrl('');
    };
  }, [open, imageFile]);

  const selectedPreset = useMemo(
    () => CROP_PRESETS.find((preset) => preset.id === presetId) || CROP_PRESETS[0],
    [presetId]
  );
  const cropAspect = selectedPreset.aspect || originalAspect || 1;

  const onCropComplete = useCallback((_croppedArea: unknown, nextCroppedAreaPixels: CropArea) => {
    setCroppedAreaPixels(nextCroppedAreaPixels);
  }, []);

  const handlePresetChange = (nextPresetId: PresetId) => {
    setPresetId(nextPresetId);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  };

  const uploadLogoBlob = async (blob: Blob): Promise<LogoUploadResult> => {
    const csrf = csrfToken || await fetchCsrfToken();
    const headers: Record<string, string> = {};

    if (!isCookieMode && token) {
      headers.Authorization = `Bearer ${token}`;
    }

    if (csrf) {
      headers['X-CSRF-Token'] = csrf;
    }

    const formData = new FormData();
    formData.append('file', blob, 'logo.png');

    const uploadResponse = await fetch(`${API_BASE_URL}/assets/logos/upload`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: formData
    });

    if (!uploadResponse.ok) {
      const errorData = await uploadResponse.json().catch(() => ({}));
      throw new Error(errorData.error || `Logo upload failed: ${uploadResponse.status}`);
    }

    return uploadResponse.json();
  };

  const handleSave = async () => {
    if (!croppedAreaPixels || !currentBusiness || !imageUrl) {
      setError('Missing crop data or business context');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const image = new Image();

      image.onload = async () => {
        try {
          if (!ctx) {
            throw new Error('Failed to create canvas context');
          }

          const cropArea = clampCropArea(croppedAreaPixels, image);
          canvas.width = cropArea.width;
          canvas.height = cropArea.height;

          ctx.drawImage(
            image,
            cropArea.x,
            cropArea.y,
            cropArea.width,
            cropArea.height,
            0,
            0,
            cropArea.width,
            cropArea.height
          );

          canvas.toBlob(async (blob) => {
            try {
              if (!blob) {
                throw new Error('Failed to process image');
              }

              const uploadData = await uploadLogoBlob(blob);
              onSave(uploadData);
              onClose();
            } catch (uploadError) {
              setError(uploadError instanceof Error ? uploadError.message : 'Failed to upload logo');
              setIsUploading(false);
            }
          }, 'image/png');
        } catch (processingError) {
          setError(processingError instanceof Error ? processingError.message : 'Failed to process image');
          setIsUploading(false);
        }
      };

      image.onerror = () => {
        setError('Failed to load image');
        setIsUploading(false);
      };

      image.src = imageUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process image');
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, border: '1px solid', borderColor: 'divider', boxShadow: '0 10px 30px rgba(16,24,40,0.12)' } }}
    >
      <DialogTitle>Crop Logo</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose a crop shape. Original keeps the full uploaded logo.
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {CROP_PRESETS.map((preset) => (
            <Button
              key={preset.id}
              size="small"
              variant={presetId === preset.id ? 'contained' : 'outlined'}
              onClick={() => handlePresetChange(preset.id)}
              disabled={isUploading}
              sx={btnSx}
            >
              {preset.label}
            </Button>
          ))}
        </Box>

        <Box sx={{ position: 'relative', height: 400, width: '100%', bgcolor: '#111827' }}>
          {imageUrl && (
            <Cropper
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              aspect={cropAspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              showGrid={presetId !== 'original'}
              objectFit="contain"
            />
          )}
        </Box>

        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2">Zoom:</Typography>
          <input
            type="range"
            min={1}
            max={4}
            step={0.1}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            style={{ flex: 1 }}
          />
          <Typography variant="body2">{Math.round(zoom * 100)}%</Typography>
          <Button
            size="small"
            onClick={() => setZoom(Math.max(1, zoom - 0.2))}
            disabled={zoom <= 1 || isUploading}
            sx={btnSx}
          >
            Zoom Out
          </Button>
          <Button
            size="small"
            onClick={() => setZoom(Math.min(4, zoom + 0.2))}
            disabled={zoom >= 4 || isUploading}
            sx={btnSx}
          >
            Zoom In
          </Button>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={isUploading} variant="text" sx={btnSx}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isUploading || !croppedAreaPixels}
          startIcon={isUploading ? <CircularProgress size={20} /> : null}
          sx={btnSx}
        >
          {isUploading ? 'Uploading...' : 'Save Logo'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
